import React, { type FC, useCallback, useDeferredValue, useEffect, useMemo } from "react";
import { Form, Grid, Loader, Segment } from "semantic-ui-react";

import { useAuth0 } from "../../../../auth/auth0";
import { useUserAuth } from "../../../../hooks/useUserAuth";

import _ from "lodash";
import { toast } from "react-toastify";

import ProtocolAnalysisTopBar from "../../../Lumber/ProtocolAnalysisTopBar";
import AnovaTable from "./AnovaTable";
import ProtocolBoxWhiskerChart from "./ProtocolBoxWhiskerChart";
import ProtocolDataNormalizationTable from "./ProtocolDataNormalizationTable";
import ProtocolSiteSpecificTable from "./ProtocolSiteSpecificTable";
import ProtocolTimecourseSiteSpecificTable from "./ProtocolTimecourseSiteSpecificTable";
import { useQuery } from "@tanstack/react-query";
import MetaTagsTable from "./MetaTagsTable";
import { getProtocolAnalysisResultsForBoxWhiskerAsync } from "../../../../apis/analysisApi";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import { getMeanComparisonForTimecourseAsync, getProtocolTukeyDataAsync } from "../../../../apis/statisticsApi";
import type { ProtocolPlotAnalysisResult } from "../../../../apis/types/analysisTypes";
import type {
	ProtocolMeanComparisonRequestRow,
	ProtocolMeanComparisonRow,
	TimecourseData,
	TimecourseDataViewModel,
	TimecourseMeanComparisonData,
} from "../../../../apis/types/statisticsTypes";
import { useProtocolAnalysis, useProtocolAnalysisDispatch } from "./ProtocolAnalysisContext";
import { useProtocolDataQuery } from "./queries";
import {
	applySelection,
	buildMeanComparisonLetters,
	buildTreatmentMatrix,
	CUSTOM_TRIAL_FILTER,
	getAssessmentOptions,
	getDataNormalizationOptions,
} from "./utils";
import type {
	AnovaData,
	BoxWhiskerChartDatum,
	BoxWhiskerCompiledRow,
	BoxWhiskerPlotRow,
	BoxWhiskerSitePlot,
	BoxWhiskerSiteRow,
	CompiledMeanComparisonPlot,
	CompiledMeanComparisonRequest,
	CompiledMeanComparisonResult,
	ProtocolBoxWhiskerProps,
	SingleTrialMeanComparison,
	TreatmentCheckedState,
	TrialCheckedState,
	UpdateCheckedTreatment,
	UpdateCheckedTrial,
} from "./types";
import sharedStyles from "./ProtocolAnalysis.module.css";

/** A plot analysis row that passed the per-treatment filter, so its treatment id is set. */
type TreatmentPlotAnalysisResult = ProtocolPlotAnalysisResult & { trialTreatmentId: number };

const buildAnovaData = (meanComparisonResult: ProtocolMeanComparisonRow | undefined): AnovaData | null => {
	if (!meanComparisonResult) {
		return null;
	}

	return {
		numeratorDf: meanComparisonResult.numeratorDF,
		denominatorDf: meanComparisonResult.denominatorDF,
		fValues: meanComparisonResult.fValues,
		pValues: meanComparisonResult.pValues,
	};
};

const ProtocolBoxWhisker: FC<ProtocolBoxWhiskerProps> = ({
	dashboardOptions,
	standCountAnalyses,
	treatmentData,
	toggleMetaFactorModal,
	trialMetadataResponse,
	metadataLoading,
}) => {
	const userAuth = useUserAuth();
	const { getTokenSilently } = useAuth0();
	const moduleNavigation = useModuleNavigation();

	const { committed, pending, display, isLettersOutdated, isNormalizationOutdated } = useProtocolAnalysis();
	const { changeSettings, changeSettingsSilently, initializeSettings, changeDisplaySettings, commitSettings } =
		useProtocolAnalysisDispatch();

	const trialIds = dashboardOptions.trialIds;

	const isGroundData = pending.assessmentKind === "grounddata";
	const isCurveModel = pending.assessmentKind === "curvemodel";
	const isStandCount = pending.assessmentKind === "standcount";
	const isAnalysisBacked = isCurveModel || isStandCount;

	//Data Sources
	//index.tsx only mounts this page once the protocol has loaded, so this reads the cached copy
	const { data: protocolDataResponse } = useProtocolDataQuery({ refetchOnMount: false });
	const protocolData = protocolDataResponse?.protocolData ?? null;
	const trialData = trialMetadataResponse;

	const needToRerunStats = isLettersOutdated;
	const needToRerunNormalization = isNormalizationOutdated;

	//TODO: This is here till we decide to move normalization to backend or not
	const dataNormalizationOptions = useMemo(
		() => getDataNormalizationOptions(dashboardOptions.dataNormalizations, treatmentData),
		[dashboardOptions, treatmentData],
	);

	//TODO: Can probably move this to display settings in context
	const hasAssessments = useMemo(() => getAssessmentOptions(dashboardOptions).length > 0, [dashboardOptions]);

	//Treatment and trial selection
	//TODO: move to context
	const hideUncheckedTrialData = _.xor(committed.selectedTrialIds, pending.selectedTrialIds).length > 0;

	const initialGroupTrialDataCheckedCount = useMemo(() => {
		if (!treatmentData) return [];

		const selectedTreatmentMatrix = committed.selectedTreatmentMatrix;

		return _.map(_.orderBy(treatmentData, "trialTreatmentId"), (t) =>
			selectedTreatmentMatrix ? !!selectedTreatmentMatrix[t.trialTreatmentId] : true,
		);
	}, [treatmentData, committed.selectedTreatmentMatrix]);

	const treatmentsChecked = useMemo((): TreatmentCheckedState[] | null => {
		if (!treatmentData) return null;

		const selectedTreatmentMatrix = pending.selectedTreatmentMatrix;

		return _.map(treatmentData, (td) => ({
			trialTreatmentId: td.trialTreatmentId,
			// TODO(types): no `!!` here (the other pages use one), so a treatment missing from the
			// matrix gets undefined rather than false
			treatmentChecked: selectedTreatmentMatrix ? selectedTreatmentMatrix[td.trialTreatmentId] : true,
		}));
	}, [treatmentData, pending.selectedTreatmentMatrix]);

	const trialsChecked = useMemo((): TrialCheckedState[] | null => {
		if (!trialIds) return null;

		const selectedTrialIds = pending.selectedTrialIds;
		return _.map(trialIds, (t) => ({
			trialId: t,
			trialChecked: selectedTrialIds ? selectedTrialIds.includes(t) : true,
		}));
	}, [trialIds, pending.selectedTrialIds]);

	//Data normalization
	const getPercentMean = useCallback(
		(trialData: ProtocolPlotAnalysisResult[]) => {
			const data = _.map(
				_.filter(trialData, (td) => {
					return (
						td.excluded !== true &&
						(td.groundDatasetAssessmentValue !== null ||
							td.curveModelAnalysisValue !== null ||
							td.standCountAnalysisResultValue !== null)
					);
				}),
				isStandCount
					? "standCountAnalysisResultValue"
					: isGroundData
						? "groundDatasetAssessmentValue"
						: "curveModelAnalysisValue",
			);
			const mean = _.meanBy(data);
			return mean;
		},
		[isGroundData, isStandCount],
	);

	const getPercentByTreatmentType = useCallback(
		(trialData: ProtocolPlotAnalysisResult[]) => {
			const treatmentTypeId = _.find(dataNormalizationOptions, (dno) => {
				return dno.value === pending.dataNormalizationId;
			})?.treatmentTypeId;
			const trialTreatment = _.find(treatmentData, (td) => {
				return td.treatmentTypeId === treatmentTypeId;
			});
			const data = _.map(
				_.filter(trialData, (td) => {
					return (
						// TODO(types): throws when the protocol has no treatment of the normalization's type
						td.trialTreatmentId === trialTreatment!.trialTreatmentId &&
						td.excluded !== true &&
						(td.groundDatasetAssessmentValue !== null ||
							td.curveModelAnalysisValue ||
							td.standCountAnalysisResultValue !== null)
					);
				}),
				isStandCount
					? "standCountAnalysisResultValue"
					: isGroundData
						? "groundDatasetAssessmentValue"
						: "curveModelAnalysisValue",
			);
			const mean = _.meanBy(data);
			return mean;
		},
		[pending.dataNormalizationId, dataNormalizationOptions, isGroundData, isStandCount, treatmentData],
	);

	const getDataNormalizationModifier = useCallback(
		(trialData: ProtocolPlotAnalysisResult[]) => {
			//Only called once the memo below has found the selected normalization
			const selectedDataNormalizationName = _.find(dataNormalizationOptions, [
				"value",
				pending.dataNormalizationId,
			])!.text.toLowerCase();
			switch (selectedDataNormalizationName) {
				case "% mean":
					return getPercentMean(trialData);
				case "% control":
				case "% commercial":
				case "% gsp":
					return getPercentByTreatmentType(trialData);
				case "none":
				case "rank order":
				default:
					//No modifications to none, just display raw data
					return 1;
			}
		},
		[pending.dataNormalizationId, dataNormalizationOptions, getPercentByTreatmentType, getPercentMean],
	);

	const getDataNormalizationMultiplier = useCallback(() => {
		//Only called once the memo below has found the selected normalization
		const selectedDataNormalizationName = _.find(dataNormalizationOptions, [
			"value",
			pending.dataNormalizationId,
		])!.text.toLowerCase();

		switch (selectedDataNormalizationName) {
			case "% mean":
			case "% control":
			case "% commercial":
			case "% gsp":
				return 100;
			case "none":
			case "rank order":
			default:
				//No modifications to none, just display raw data
				return 1;
		}
	}, [pending.dataNormalizationId, dataNormalizationOptions]);

	//Assessment data
	const { data: rawTrialData, isFetching: loading } = useQuery({
		// TODO(types): pending.plannedTimingId is missing from the key although queryFn sends it, so
		// changing only the growth phase serves stale data
		queryKey: [
			"protocol box whisker data",
			pending.assessmentValue,
			pending.analysisId,
			pending.analysisTypeId,
			userAuth.currentClientId,
			moduleNavigation.protocolId,
			committed.removeDamagedOrExcluded,
			committed.removeOutliers,
			committed.outlierType,
			pending.groundDataAssessmentId,
			pending.plannedTimingId,
			pending.curveModelAnalysisId,
		],
		queryFn: async () => {
			//The endpoint takes "" rather than null for ids that do not apply to this kind
			//Ids and the client are checked by `enabled` (protocolId is string | null from the hook)
			// TODO(types): the per-kind ids can still be null here (e.g. no growth phase picked), which
			// the URL template sends as "null"
			return getProtocolAnalysisResultsForBoxWhiskerAsync(
				userAuth.currentClientId!,
				moduleNavigation.protocolId!,
				await getTokenSilently(),
				isGroundData ? (pending.groundDataAssessmentId as string) : "",
				isGroundData ? (pending.plannedTimingId as string) : "",
				isCurveModel ? (pending.curveModelAnalysisId as string) : "",
				isAnalysisBacked ? (pending.analysisId as string) : "",
				false,
				isAnalysisBacked ? (pending.analysisTypeId as string) : "",
				false,
				committed.removeOutliers,
				committed.removeDamagedOrExcluded,
				committed.outlierType,
			);
		},
		select: (data) => {
			const filteredTreatments: ProtocolPlotAnalysisResult[][] = [];

			const grouped = _.values(_.groupBy(data.plotAnalysisResults, "trialId"));

			_.forEach(grouped, (r) => {
				//Filter out trials that do not have data for selected analysis
				const f = _.filter(r, (trial) => {
					return (
						// TODO(types): a null trialTreatmentId coerces to 0 and passes; protocolData is only
						// null when the protocol isn't found, and index.tsx doesn't mount the page then
						Number(trial.trialTreatmentId) <= protocolData!.treatments &&
						((trial.analysisId === pending.analysisId && trial.analysisTypeId === pending.analysisTypeId) ||
							(!!trial.assessmentId && trial.groundDatasetAssessmentValue !== null)) &&
						trial.excludeFromAssessment === false
					);
				});
				if (f?.length > 0) {
					filteredTreatments.push(f);
				}
			});
			return filteredTreatments;
		},
		enabled: !!pending.assessmentValue && !!userAuth.currentClientId && !!moduleNavigation.protocolId,
	});

	//Normalized site rows (one per trial x treatment, holding its mean) and plot rows (one per plot).
	//The checked flags are added downstream by the filtered memos
	const { siteSpecificData, trialPlotSpecificData } = useMemo(() => {
		const hasNormalization = _.some(dataNormalizationOptions, ["value", pending.dataNormalizationId]);
		if (!rawTrialData || !trialData || !protocolData || !hasNormalization) {
			return { siteSpecificData: null, trialPlotSpecificData: null };
		}

		const getValue = (row: ProtocolPlotAnalysisResult) =>
			isStandCount
				? row.standCountAnalysisResultValue
				: isGroundData
					? row.groundDatasetAssessmentValue
					: row.curveModelAnalysisValue;
		const multiplier = getDataNormalizationMultiplier();

		const siteData: BoxWhiskerSiteRow[] = [];
		const plotObjects: BoxWhiskerPlotRow[] = [];

		_.forEach(rawTrialData, (td) => {
			if (!td?.length) return;

			const dataNormalization = getDataNormalizationModifier(td);
			// TODO(types): the selected kind's value can be null (the row filter accepts any of the three
			// values), which coerces to 0
			const normalize = (value: number | null) => (Number(value) / dataNormalization) * multiplier;
			const siteMetadata = _.find(trialData, (tmd) => tmd.trialId === td[0].trialId)?.trialInfo;

			for (let i = 1; i <= protocolData.treatments; ++i) {
				const trialTreatmentData = _.filter(td, (trial): trial is TreatmentPlotAnalysisResult => {
					return (
						trial.trialTreatmentId === i &&
						trial.excluded !== true &&
						(trial.groundDatasetAssessmentValue !== null ||
							trial.curveModelAnalysisValue !== null ||
							trial.standCountAnalysisResultValue !== null)
					);
				});

				_.forEach(trialTreatmentData, (t) => {
					const metadata = _.find(trialData, (tmd) => tmd.trialId === t.trialId)?.trialInfo;
					plotObjects.push({
						trialId: t.trialId,
						trialName: t.trialName,
						plotReplicate: t.plotReplicate,
						plotName: t.plotName,
						trialTreatmentId: i,
						average: normalize(getValue(t)),
						excluded: false,
						region: t.region,
						country: t.country,
						countryCode: t.countryCode,
						city: t.city,
						cooperatorName: metadata?.cooperatorName,
						excludeFromAssessment: t.excludeFromAssessment,
						// TODO(types): the backend always sends the outlier flags as false, so outlier
						// filtering downstream is a no-op
						isOutlier3: t.isOutlier3,
						isOutlier1_5: t.isOutlier1_5,
					});
				});

				const plots = _.map(
					trialTreatmentData,
					(d): BoxWhiskerSitePlot => ({
						plotReplicate: d.plotReplicate,
						plotId: d.plotId,
						plotName: d.plotName,
						fieldId: d.fieldId,
						analysisValue: normalize(getValue(d)),
						trialTreatmentId: d.trialTreatmentId,
						assessmentId: isStandCount ? d.analysisId : isGroundData ? d.assessmentId : d.curveModelName,
						trialId: d.trialId,
						dataNormalizationId: pending.dataNormalizationId,
						excludeFromAssessment: d.excludeFromAssessment,
						isOutlier3: d.isOutlier3,
						isOutlier1_5: d.isOutlier1_5,
					}),
				);

				const hasData = trialTreatmentData.length > 0;
				siteData.push({
					trialId: td[0].trialId,
					trialName: td[0].trialName,
					trialTreatmentId: i,
					//A null average marks a treatment with no data in this trial; the tables filter on it.
					average: hasData ? _.meanBy(trialTreatmentData, (t) => normalize(getValue(t))) : null,
					excluded: !hasData,
					region: td[0].region,
					country: td[0].country,
					countryCode: td[0].countryCode,
					city: td[0].city,
					cooperatorName: siteMetadata?.cooperatorName,
					plots,
					// TODO(types): curve model flight rows sort first, so td[0] is a flight row: the name is the
					// analysisName and the id null, and the "MG50" reversal check never matches
					assessmentName: td[0].assessmentName ?? td[0].curveModelName ?? td[0].analysisName,
					assessmentId: isStandCount ? td[0].analysisId : isGroundData ? td[0].assessmentId : td[0].curveModelName,
				});
			}
		});

		return { siteSpecificData: siteData, trialPlotSpecificData: plotObjects };
	}, [
		dataNormalizationOptions,
		pending.dataNormalizationId,
		rawTrialData,
		trialData,
		protocolData,
		getDataNormalizationMultiplier,
		isStandCount,
		isGroundData,
		getDataNormalizationModifier,
	]);

	const filteredTrialPlotSpecificData = useMemo(
		() => applySelection(trialPlotSpecificData, committed.selectedTreatmentMatrix, committed.selectedTrialIds),
		[trialPlotSpecificData, committed.selectedTreatmentMatrix, committed.selectedTrialIds],
	);

	const filteredSiteSpecificData = useMemo(
		() => applySelection(siteSpecificData, committed.selectedTreatmentMatrix, committed.selectedTrialIds),
		[siteSpecificData, committed.selectedTreatmentMatrix, committed.selectedTrialIds],
	);

	//What the site specific and data normalization tables show
	const pendingSiteSpecificData = useMemo(
		() => applySelection(siteSpecificData, pending.selectedTreatmentMatrix, pending.selectedTrialIds),
		[siteSpecificData, pending.selectedTreatmentMatrix, pending.selectedTrialIds],
	);

	//Feeds only the data normalization table, so it follows the pending selection too
	const compiledData = useMemo((): BoxWhiskerCompiledRow[] | null => {
		if (!pendingSiteSpecificData?.length || !treatmentData || !protocolData) return null;

		//Unchecked trials drop out of the averages and the missing-treatment check
		const sitesByTreatment = _.groupBy(_.filter(pendingSiteSpecificData, "trialChecked"), "trialTreatmentId");
		const trialsInAssessment = _.max(_.map(sitesByTreatment, (sites) => sites.length));
		//TODO: convert to list of reversed assessments
		// TODO(types): never matches for curve models, see assessmentName above
		const reversed = !!pendingSiteSpecificData[0]?.assessmentName?.includes("MG50");

		const averageData = _.map(_.range(1, protocolData.treatments + 1), (trialTreatmentId) => {
			const treatment = _.find(treatmentData, { trialTreatmentId });
			const sites = sitesByTreatment[trialTreatmentId] ?? [];
			const sitesWithPlots = _.filter(sites, (s) => s.plots?.length > 0);

			return {
				trialTreatmentId,
				treatmentTypeName: treatment?.treatmentTypeName,
				treatmentName: treatment?.name,
				average: _.meanBy(sitesWithPlots, "average"),
				assessmentData: _.map(sitesWithPlots, (s) => ({ plots: s.plots })),
				treatmentChecked: pending.selectedTreatmentMatrix ? !!pending.selectedTreatmentMatrix[trialTreatmentId] : true,
				reversed,
				hasMissingTreatments: sites.length > 0 && (sites.length !== trialsInAssessment || _.some(sites, "excluded")),
			};
		});

		const normalizationName = _.find(dataNormalizationOptions, [
			"value",
			pending.dataNormalizationId,
		])?.text.toLowerCase();
		if (normalizationName === "rank order") {
			_.forEach(_.orderBy(averageData, ["average"], ["desc"]), (ad, idx) => {
				ad.average = idx + 1;
				ad.reversed = true;
			});
		}

		return averageData;
	}, [
		pendingSiteSpecificData,
		treatmentData,
		protocolData,
		dataNormalizationOptions,
		pending.dataNormalizationId,
		pending.selectedTreatmentMatrix,
	]);

	const trialInfo = useMemo(
		() =>
			_.sortBy(
				_.map(
					_.filter(trialData, (td) =>
						_.map(_.uniqBy(trialPlotSpecificData, "trialId"), "trialId").includes(td.trialInfo.trialId),
					),
					(td) => td.trialInfo,
				),
				["farmCountry", "farmRegion", "farmCity", "trialName", "cooperatorName", "trialId"],
			),
		[trialData, trialPlotSpecificData],
	);

	//Mean comparisons
	//HACK: since we are hijacking, the prop names are not gonna add up
	const aucMeanComparisonRequestData = useMemo((): TimecourseDataViewModel & { data: TimecourseData[] } => {
		const data = _.map(
			_.filter(filteredTrialPlotSpecificData, (tpsd) => {
				return tpsd.treatmentChecked;
			}),
			(tpsd): TimecourseData => {
				return {
					flightId: tpsd.trialId,
					plotId: tpsd.trialId,
					dayOfYear: -1,
					trialTreatmentId: tpsd.trialTreatmentId,
					// TODO(types): plotReplicate can be null, but the endpoint takes a number
					plotReplicate: tpsd.plotReplicate as number,
					range: -1,
					value: tpsd.average,
					excludeFromAssessment: false,
					isOutlier3: false,
					isOutlier1_5: false,
				};
			},
		);
		return { data };
	}, [filteredTrialPlotSpecificData]);

	const { data: singleTrialAnovaValues, isFetching: singleTrialTukeyRunning } = useQuery({
		queryKey: [
			"protocol trial mean comparison data",
			aucMeanComparisonRequestData,
			committed.removeDamagedOrExcluded,
			committed.alphaOptionId,
			committed.meanComparisonOptionId,
			committed.removeOutliers,
		],
		// TODO(types): `enabled` does not check the client, mean comparison or alpha, so they can be
		// sent as "null"
		queryFn: async () =>
			getMeanComparisonForTimecourseAsync(
				aucMeanComparisonRequestData,
				userAuth.currentClientId as string,
				"00000000-0000-0000-0000-000000000000",
				committed.meanComparisonOptionId as string,
				committed.alphaOptionId as string,
				await getTokenSilently(),
				committed.removeDamagedOrExcluded,
				committed.removeOutliers,
				committed.outlierType,
			),
		select: ({ data }) => {
			// TODO(types): data can be the failure reason string; `.letters` is then undefined and
			// this maps to []
			const res = _.map((data as TimecourseMeanComparisonData).letters, (trial): SingleTrialMeanComparison => {
				return {
					trialId: trial.flightId,
					letteringPairs: _.map(trial.treatments, (trt) => {
						return { ...trt, trialId: trial.flightId, tukeyLetters: trt.letter, treatmentId: trt.trialTreatmentId };
					}),
					pValue: trial.treatments[0].pValue,
					cv: trial.treatments[0].cv,
					mean: trial.treatments[0].mean,
				};
			});
			return res;
		},
		enabled: aucMeanComparisonRequestData.data.length > 0,
	});

	//The compiled mean comparison request, one group per assessment/planned timing. Inclusion comes
	//from the *committed* selection, not the live checkboxes, so the letters only re-run when new
	//site data lands or Update Analytics commits a selection
	const compiledMeanComparisonRequest = useMemo((): CompiledMeanComparisonRequest<BoxWhiskerSitePlot> => {
		const sitesWithPlots = _.filter(siteSpecificData, (ssd) => {
			const trialIncluded = committed.selectedTrialIds ? committed.selectedTrialIds.includes(ssd.trialId) : true;

			return trialIncluded && ssd.plots?.length > 0;
		});

		const sitesByTreatment = _.groupBy(sitesWithPlots, "trialTreatmentId");
		//A treatment only takes part when it has data in every trial of the assessment
		const trialsInAssessment = _.max(_.map(sitesByTreatment, (sites) => sites.length));

		const plotData: CompiledMeanComparisonPlot<BoxWhiskerSitePlot>[] = [];
		_.forEach(sitesByTreatment, (sites, trialTreatmentId) => {
			const treatmentIncluded = committed.selectedTreatmentMatrix
				? !!committed.selectedTreatmentMatrix[Number(trialTreatmentId)]
				: true;

			if (!treatmentIncluded || sites.length !== trialsInAssessment) {
				return;
			}

			_.forEach(sites, (site) => {
				// TODO(types): site rows never set plannedTimingId, so the groups are keyed
				// "<assessmentId> - undefined"
				_.forEach(site.plots, (plot) => plotData.push({ ...plot, plannedTimingId: site.plannedTimingId }));
			});
		});

		return {
			groups: _.values(_.groupBy(plotData, (pd) => `${pd.assessmentId} - ${pd.plannedTimingId}`)),
			hasMultipleTrials: _.uniqBy(plotData, "trialId").length > 1,
		};
	}, [siteSpecificData, committed.selectedTreatmentMatrix, committed.selectedTrialIds]);

	const {
		data: compiledMeanComparison,
		isFetching: compiledTukeyRunning,
		isError: compiledTukeyFailed,
		isSuccess: compiledTukeySucceeded,
	} = useQuery({
		queryKey: [
			"protocol compiled mean comparison data",
			moduleNavigation.protocolId,
			userAuth.currentClientId,
			compiledMeanComparisonRequest.groups,
			committed.alphaOptionId,
			committed.meanComparisonOptionId,
		],
		queryFn: async () => {
			const accessToken = await getTokenSilently();

			//The endpoint takes one assessment/planned timing group at a time
			//TODO: Convert to a single call
			//The alpha, mean comparison and client are checked by `enabled`
			return Promise.all(
				_.map(compiledMeanComparisonRequest.groups, (plotData) =>
					getProtocolTukeyDataAsync(
						// TODO(types): plotReplicate and dataNormalizationId can be null; the request row
						// types them as non-null
						plotData as ProtocolMeanComparisonRequestRow[],
						committed.alphaOptionId!,
						committed.meanComparisonOptionId!,
						userAuth.currentClientId!,
						accessToken,
					),
				),
			);
		},
		select: (responses): CompiledMeanComparisonResult => {
			// TODO(types): data is null on a bad argument (e.g. all values zero), which throws here
			return {
				letters: buildMeanComparisonLetters(_.flatMap(responses, (r) => r.data!.protocolData ?? [])),
				trialLetters: buildMeanComparisonLetters(_.flatMap(responses, (r) => r.data!.trialData ?? [])),
				anovaData: buildAnovaData(_.last(responses)?.data?.protocolData?.[0]),
			};
		},
		enabled:
			compiledMeanComparisonRequest.groups.length > 0 &&
			compiledMeanComparisonRequest.hasMultipleTrials &&
			!!committed.alphaOptionId &&
			!!committed.meanComparisonOptionId &&
			!!userAuth.currentClientId,
	});

	//Stringified so a null assessment indexes the "null" key
	const assessmentKey = String(pending.assessmentValue);

	const compiledMeanComparisonData = compiledMeanComparison?.letters ?? {};
	const compiledTrialMeanComparisonData = compiledMeanComparison?.trialLetters ?? {};
	const anovaData = compiledMeanComparison?.anovaData ?? null;

	const oneTrialDatasetError =
		compiledMeanComparisonRequest.groups.length > 0 && !compiledMeanComparisonRequest.hasMultipleTrials;
	const multiTrialDatasetError = compiledTukeyFailed;
	const tukeyRunning = singleTrialTukeyRunning || compiledTukeyRunning;

	//Chart inputs follow the pending selection through deferred copies, so the checkbox and tables
	//paint first and the charts rebuild in a background render. A chart redraws whenever it gets a
	//new `data` or `tukeyData` array, so everything here is memoized.
	const chartTreatmentMatrix = useDeferredValue(pending.selectedTreatmentMatrix);
	const chartTrialIds = useDeferredValue(pending.selectedTrialIds);

	const chartSiteSpecificData = useMemo(
		() => applySelection(siteSpecificData, chartTreatmentMatrix, chartTrialIds),
		[siteSpecificData, chartTreatmentMatrix, chartTrialIds],
	);

	const chartTrialPlotSpecificData = useMemo(
		() => applySelection(trialPlotSpecificData, chartTreatmentMatrix, chartTrialIds),
		[trialPlotSpecificData, chartTreatmentMatrix, chartTrialIds],
	);

	const allTreatmentsUnchecked = chartTreatmentMatrix
		? !_.some(treatmentData, (t) => chartTreatmentMatrix[t.trialTreatmentId])
		: false;

	const compiledChartData = useMemo(
		(): BoxWhiskerChartDatum[] =>
			_.filter<BoxWhiskerChartDatum>(
				display.showAverages
					? allTreatmentsUnchecked
						? chartSiteSpecificData
						: _.filter(chartSiteSpecificData, (ssd) => {
								return ssd.treatmentChecked && ssd.trialChecked && ssd.plots.length > 0;
							})
					: allTreatmentsUnchecked
						? chartTrialPlotSpecificData
						: _.filter(chartTrialPlotSpecificData, (tsd) => {
								return tsd.treatmentChecked && tsd.trialChecked;
							}),
			),
		[display.showAverages, allTreatmentsUnchecked, chartSiteSpecificData, chartTrialPlotSpecificData],
	);

	const compiledChartAllTreatmentData = useMemo(
		(): BoxWhiskerChartDatum[] =>
			_.filter<BoxWhiskerChartDatum>(display.showAverages ? chartSiteSpecificData : chartTrialPlotSpecificData),
		[display.showAverages, chartSiteSpecificData, chartTrialPlotSpecificData],
	);

	//Per trial chart inputs, keyed by trialId. These only follow the treatment selection, so ticking
	//a trial redraws only the compiled chart. A trial without two-way letters gets [] rather than
	//undefined, since the chart waits on an empty list but draws without letters on undefined.
	const trialChartData = useMemo(() => {
		const plotsByTrial = _.groupBy(applySelection(trialPlotSpecificData, chartTreatmentMatrix, null), "trialId");
		const trialLetters = compiledMeanComparison?.trialLetters?.[assessmentKey];
		const lettersByTrial =
			display.anovaType === "two-way"
				? _.groupBy(trialLetters, "trialId")
				: _.mapValues(_.keyBy(singleTrialAnovaValues, "trialId"), "letteringPairs");

		return _.mapValues(_.keyBy(trialData, "trialInfo.trialId"), (td, trialId) => ({
			data: plotsByTrial[trialId] ?? [],
			tukeyData: display.anovaType === "two-way" ? (lettersByTrial[trialId] ?? []) : lettersByTrial[trialId],
		}));
	}, [
		trialPlotSpecificData,
		chartTreatmentMatrix,
		compiledMeanComparison,
		assessmentKey,
		display.anovaType,
		singleTrialAnovaValues,
		trialData,
	]);

	//Outcome reporting for the compiled run
	useEffect(() => {
		if (compiledTukeyRunning) {
			return;
		}

		if (oneTrialDatasetError) {
			//Adding a toast id so that we do not get multiples of the same messages
			toast.error("Cannot run multi-trial mean comparisons when only 1 trial dataset is available", {
				toastId: "OneTrialDatasetErrorSend",
			});
		} else if (compiledTukeyFailed) {
			//Adding a toast id so that we do not get multiples of the same messages
			toast.error("There was an issue getting mean comparison values for the multi-trial datasets", {
				toastId: "MultiTrialDatasetErrorSend",
			});
		} else if (!compiledTukeySucceeded) {
			//Nothing has been asked of the endpoint yet
			return;
		}

		toast.success("All data has finished loading.", { toastId: "allFinished" });
	}, [oneTrialDatasetError, compiledTukeyRunning, compiledTukeyFailed, compiledTukeySucceeded]);

	//Page lifecycle

	useEffect(() => {
		if (trialData && !!display.trialFilterId && display.trialFilterId !== CUSTOM_TRIAL_FILTER) {
			const passingTrialIds = _.map(
				_.filter(
					trialData,
					({ trialInfo }) =>
						!display.trialFilters.status.includes(trialInfo.trialExclusionStatusId) &&
						!display.trialFilters.response.includes(trialInfo.trialSiteResponseId) &&
						!display.trialFilters.quality.includes(trialInfo.trialDataQualityId),
				),
				(td) => td.trialInfo.trialId,
			);
			if (committed.selectedTrialIds === null) {
				//Bypasses committed vs pending
				initializeSettings({ selectedTrialIds: passingTrialIds });
			} else {
				changeSettingsSilently({ selectedTrialIds: passingTrialIds });
			}
		}
	}, [
		display.trialFilterId,
		display.trialFilters,
		trialData,
		committed.selectedTrialIds,
		changeSettingsSilently,
		initializeSettings,
	]);

	//Handlers
	//Both handlers take one id or a list and dispatch once
	//Note that each call reads `pending` from this render,
	//so calling them in a loop would leave only the last change standing
	const updateCheckedTreatment: UpdateCheckedTreatment = (value, trialTreatmentIdOrIds) => {
		const matrix = [...(pending.selectedTreatmentMatrix ?? buildTreatmentMatrix(treatmentData))];
		for (const trialTreatmentId of _.castArray(trialTreatmentIdOrIds)) {
			matrix[trialTreatmentId] = value;
		}
		changeSettings({ selectedTreatmentMatrix: matrix });
	};

	const updateTrialsChecked: UpdateCheckedTrial = (value, trialIdOrIds, silent = false) => {
		if (!silent) changeDisplaySettings({ trialFilterId: CUSTOM_TRIAL_FILTER });
		const ids = _.castArray(trialIdOrIds);
		const current = pending.selectedTrialIds ?? trialIds;
		changeSettings({ selectedTrialIds: value ? _.union(current, ids) : _.difference(current, ids) });
	};

	//Mean comparisons need at least two treatments and two trials to compare
	const handleCommit = () => {
		if (
			_.filter(treatmentsChecked, (tc) => tc.treatmentChecked).length < 2 ||
			_.filter(trialsChecked, (tc) => tc.trialChecked).length < 2
		) {
			toast.error("At least two treatments and two trials need to be selected");
			return;
		}

		commitSettings();
	};

	return !hasAssessments ? (
		<Segment style={{ width: "100%" }}>
			<p>{"We could not find data for this protocol."}</p>
		</Segment>
	) : (
		<Segment basic style={{ padding: 0 }}>
			<Form>
				<ProtocolAnalysisTopBar
					mode="boxWhisker"
					standCountAnalyses={standCountAnalyses}
					dashboardOptions={dashboardOptions}
					treatments={treatmentData}
					loading={loading}
					disabled={tukeyRunning || metadataLoading}
					onCommit={handleCommit}
				/>
				<Grid>
					{loading ? (
						<Loader active inline="centered" />
					) : !siteSpecificData?.length ? (
						<Segment basic>
							<p>{"Please select an Assessment and Planned Timing Method to view data."}</p>
						</Segment>
					) : (
						<>
							<Grid.Row style={{ paddingTop: "1em" }}>
								<Grid.Column width="6" verticalAlign="middle">
									<hr style={{ width: "100%" }} />
								</Grid.Column>
								<Grid.Column width="4" textAlign="center">
									<h2>Compiled Protocol Data</h2>
								</Grid.Column>
								<Grid.Column width="6" verticalAlign="middle">
									<hr style={{ width: "100%" }} />
								</Grid.Column>
							</Grid.Row>
							{filteredSiteSpecificData != null &&
								filteredSiteSpecificData?.length > 0 &&
								compiledData != null &&
								compiledData.length > 0 &&
								trialPlotSpecificData != null &&
								trialPlotSpecificData.length > 0 && (
									<Grid.Row>
										<Grid.Column width="8">
											<Form.Field className="aucTableContainer">
												<ProtocolDataNormalizationTable
													compiledData={compiledData}
													tukeyData={compiledMeanComparisonData}
													disableCheckBoxes={tukeyRunning}
													selectedAssessmentOption={pending.assessmentValue}
													dataNormalization={
														//Site data only exists once the selected normalization was found
														_.find(dataNormalizationOptions, (dno) => {
															return dno.value === pending.dataNormalizationId;
														})!.text
													}
													updateCheckedTreatment={updateCheckedTreatment}
													treatmentsChecked={treatmentsChecked}
													hideLetters={isLettersOutdated}
													updateCheckedTrial={updateTrialsChecked}
													hideUncheckedTrialData={hideUncheckedTrialData}
													needToRerunStats={needToRerunStats}
													needToRerunNormalization={needToRerunNormalization}
													oneTrialDatasetError={oneTrialDatasetError}
													collapseView={display.collapseView}
												/>
											</Form.Field>
										</Grid.Column>
										<Grid.Column width="8">
											<Grid.Row>
												{siteSpecificData && trialPlotSpecificData ? (
													<ProtocolBoxWhiskerChart
														tukeyData={compiledMeanComparisonData[assessmentKey]}
														tukeyRunning={tukeyRunning}
														data={compiledChartData}
														hideMeanComparisonResults={isLettersOutdated}
														assessmentName={display.assessmentName}
														axisYTitle={`${display.assessmentName?.split(" (")[0]} (${
															_.find(dataNormalizationOptions, ["key", pending.dataNormalizationId])?.text
														})`}
														chartHeight={500}
														allTreatmentData={compiledChartAllTreatmentData}
														isCompiled={display.showAverages ? "averages" : "allReps"}
														disableColors={display.disableColors}
														initialGroupTrialDataCheckedCount={initialGroupTrialDataCheckedCount}
														hideUncheckedTrialData={hideUncheckedTrialData}
														needToRerunStats={needToRerunStats}
														allTreatmentsUnchecked={allTreatmentsUnchecked}
														needToRerunNormalization={needToRerunNormalization}
													/>
												) : null}
											</Grid.Row>
											<Grid.Row>
												<Grid>
													<Grid.Column width="4">
														<Form.Radio
															checked={display.showAverages}
															label="Trial Averages"
															disabled={loading || tukeyRunning || metadataLoading}
															onChange={() => {
																changeDisplaySettings({ showAverages: true });
															}}
														/>
														<Form.Radio
															checked={!display.showAverages}
															label="All Replicates"
															disabled={loading || tukeyRunning || metadataLoading}
															onChange={() => {
																changeDisplaySettings({ showAverages: false });
															}}
														/>
														<Form.Checkbox
															checked={display.disableColors}
															onChange={() => changeDisplaySettings({ disableColors: !display.disableColors })}
															label="Disable Graph Colors"
															toggle
														/>
													</Grid.Column>
													<Grid.Column className="aucTableContainer" width="12">
														<AnovaTable
															anovaData={anovaData}
															hideData={tukeyRunning || needToRerunStats || needToRerunNormalization}
															wasThereAnError={multiTrialDatasetError || oneTrialDatasetError}
														/>
													</Grid.Column>
												</Grid>
											</Grid.Row>
										</Grid.Column>
									</Grid.Row>
								)}
							<Grid.Row>
								<Grid.Column width="7" verticalAlign="middle">
									<hr style={{ width: "100%" }} />
								</Grid.Column>
								<Grid.Column width="2" textAlign="center">
									<h2>Trial Data</h2>
								</Grid.Column>
								<Grid.Column width="7" verticalAlign="middle">
									<hr style={{ width: "100%" }} />
								</Grid.Column>
							</Grid.Row>

							{loading ? null : (
								<Grid.Row>
									<Grid.Column width="16" textAlign="center">
										<h4>{`${display.assessmentName?.split(" (")[0]} (${
											_.find(dataNormalizationOptions, ["key", pending.dataNormalizationId])?.text
										})`}</h4>
									</Grid.Column>
									<Grid.Column width="8" style={{ display: "none" }}>
										<Form.Group inline>
											<label>Perform Mean Comparisons as: </label>
											<Form.Radio
												checked={display.anovaType === "two-way"}
												label={"Single Multi-Location Data Set"}
												disabled={loading || tukeyRunning || metadataLoading}
												onChange={() => changeDisplaySettings({ anovaType: "two-way" })}
											></Form.Radio>
											<Form.Radio
												checked={display.anovaType === "one-way"}
												label={"Seperate, Isolated Data Sets"}
												disabled={loading || tukeyRunning || metadataLoading}
												onChange={() => changeDisplaySettings({ anovaType: "one-way" })}
											></Form.Radio>
										</Form.Group>
									</Grid.Column>
								</Grid.Row>
							)}
							{filteredSiteSpecificData && filteredSiteSpecificData?.length > 0 ? (
								<div className={sharedStyles.trialTableWrapper}>
									<Grid.Row className="aucTableContainer" style={{ padding: 0 }}>
										<MetaTagsTable trialInfo={trialInfo} collapseView={display.collapseView} />
									</Grid.Row>
									<Grid.Row className="aucTableContainer" style={{ padding: 0 }}>
										<ProtocolSiteSpecificTable
											siteSpecificData={pendingSiteSpecificData}
											tukeyData={
												display.anovaType === "two-way"
													? compiledTrialMeanComparisonData[assessmentKey]
													: _.flatten(
															_.map(singleTrialAnovaValues, (data) => {
																return data.letteringPairs;
															}),
														)
											}
											tukeyDataLoading={tukeyRunning}
											disableCheckBoxes={tukeyRunning}
											updateCheckedTrial={updateTrialsChecked}
											appendedPercent={_.find(dataNormalizationOptions, [
												"value",
												pending.dataNormalizationId,
											])!.text.includes("%")}
											showPValues={display.anovaType === "one-way"}
											hideMeanComparisonResults={isLettersOutdated}
											needToRerunNormalization={needToRerunNormalization}
											needToRerunStats={needToRerunStats}
											collapseView={display.collapseView}
										/>
									</Grid.Row>
								</div>
							) : null}
							<Grid.Row>
								<Grid.Column width="6" verticalAlign="middle">
									<hr style={{ width: "100%" }} />
								</Grid.Column>
								<Grid.Column width="4" textAlign="center">
									<h2>Trial Box Whisker Charts</h2>
								</Grid.Column>
								<Grid.Column width="6" verticalAlign="middle">
									<hr style={{ width: "100%" }} />
								</Grid.Column>
							</Grid.Row>
							{trialPlotSpecificData && trialPlotSpecificData?.length > 0
								? _.map(
										_.filter(trialData, (td) =>
											_.map(_.uniqBy(trialPlotSpecificData, "trialId"), "trialId").includes(td.trialInfo.trialId),
										),
										(ftd) => {
											return (
												<Grid.Row key={ftd.trialInfo.trialId}>
													<Grid.Column className="aucTableContainer" width="3">
														<ProtocolTimecourseSiteSpecificTable
															trialId={ftd.trialInfo.trialId}
															trialName={ftd.trialInfo.trialName}
															cooperatorName={ftd.trialInfo.cooperatorName}
															farmCity={ftd.trialInfo.farmCity}
															farmRegion={ftd.trialInfo.farmRegion}
															farmCountryCode={ftd.trialInfo.farmCountryCode}
															clientId={userAuth.currentClientId}
															hasExclusions={ftd.trialInfo.hasExclusions}
															hasFlaggings={ftd.trialInfo.hasFlaggings}
															hasPartialExclusions={ftd.trialInfo.hasPartialExclusions}
															hasPartialFlaggings={ftd.trialInfo.hasPartialFlaggings}
															toggleMetaFactorModal={toggleMetaFactorModal}
														/>
													</Grid.Column>
													<Grid.Column width="13">
														<ProtocolBoxWhiskerChart
															data={trialChartData[ftd.trialInfo.trialId].data}
															tukeyData={trialChartData[ftd.trialInfo.trialId].tukeyData}
															tukeyRunning={tukeyRunning}
															hideMeanComparisonResults={isLettersOutdated}
															disableColors={display.disableColors}
															needToRerunStats={needToRerunStats}
															initialGroupTrialDataCheckedCount={initialGroupTrialDataCheckedCount}
															needToRerunNormalization={needToRerunNormalization}
															chartHeight={300}
															assessmentName={display.assessmentName}
															trialName={ftd.trialInfo.trialName}
														/>
													</Grid.Column>
												</Grid.Row>
											);
										},
									)
								: null}
						</>
					)}
				</Grid>
			</Form>
		</Segment>
	);
};

export default ProtocolBoxWhisker;
